import { Dispatch } from 'redux';
import { ConflictErrorErrorEnum, CreateUserRequest } from '@cdaas/api-clients/dist/asset-user-openapi';
import { isAxiosError } from 'axios';

import { showGrowlNotificationTranslated, AppThunk } from './';
import { User, UsersActions } from '../../types';
import {
  createUser,
  deleteUser as deleteUserApi,
  devLogger,
  getUsersFromOrganizationOrUserGroup,
  moveUsersToUserGroup,
  sanitizeEmployeeId,
  updateUserDetails as updateUserDetailsApi,
} from '../../utils';
import * as actionTypes from './actionTypes';

export const usersRequestError = (errorMessage: string): UsersActions => ({
  type: actionTypes.USERS_REQUEST_USERS_ERROR,
  errorMessage,
});

export const usersRequestStart = (): UsersActions => ({
  type: actionTypes.USERS_REQUEST_START,
});

export const updateUser = (user: User, userId?: string): UsersActions => ({
  type: actionTypes.USERS_UPDATE_USER,
  user,
  userId: userId || user.id,
});

export const addEmptyUser = (): UsersActions => ({
  type: actionTypes.USERS_ADD_USER,
});

export const deleteUser = (userId: string): UsersActions => ({
  type: actionTypes.USERS_DELETE_USER,
  userId,
});

export const deleteSavedUser = (userId: string): UsersActions => ({
  type: actionTypes.USERS_DELETE_SAVED_USER,
  userId,
});

export const getUsersSuccess = (users: User[]): UsersActions => {
  return {
    type: actionTypes.USERS_GET_USERS_SUCCESS,
    users,
  };
};

export const getUserGroupUsersSuccess = (users: User[]): UsersActions => {
  return {
    type: actionTypes.USERS_GET_USERGROUP_USERS_SUCCESS,
    users,
  };
};

export const getAllUsers = (): AppThunk<Promise<void>> => async (dispatch: Dispatch) => {
  dispatch(usersRequestStart());
  const users = await getUsersFromOrganizationOrUserGroup('organization');
  if (!users) {
    dispatch(showGrowlNotificationTranslated('apiErrors:user.getAllUsersError', 'error'));
    dispatch(usersRequestError('Get organization all user api error'));
    return;
  }

  dispatch(getUsersSuccess(users));
};

export const getUserGroupUsers =
  (userGroupId: string): AppThunk<Promise<void>> =>
  async (dispatch: Dispatch) => {
    dispatch(usersRequestStart());
    const users = await getUsersFromOrganizationOrUserGroup('group', userGroupId);
    if (!users) {
      dispatch(showGrowlNotificationTranslated('apiErrors:user.getUsersForUserGroupError', 'error'));
      dispatch(usersRequestError('Get user group all user api error'));
      return;
    }

    dispatch(getUserGroupUsersSuccess(users));
  };

export const saveUser =
  (user: User): AppThunk<Promise<void>> =>
  async (dispatch: Dispatch) => {
    dispatch(usersRequestStart());
    let updatedUser: User;
    const userGroupId = user.user_group?.id;
    const employeeId = sanitizeEmployeeId(user.employee_id);

    const handleApiError = (message: string, errorKey: string): void => {
      dispatch(showGrowlNotificationTranslated(`apiErrors:user.${errorKey}`, 'error'));
      dispatch(usersRequestError(message));
    };

    if (user.newItem) {
      const userToSave: CreateUserRequest = {
        email: user.email,
        phone_number: '+' + user.country_calling_code! + user.phone_number!,
        employee_id: employeeId ?? undefined,
        allow_access_without_bank_id: false,
        invoicing_account: user.invoicing_account?.id,
        user_roles: [],
        allowed_procurements: [],
      };
      const savedUser = await createUser(userToSave, userGroupId!);

      if (isAxiosError(savedUser) || savedUser === null) {
        switch (savedUser?.response?.data?.error) {
          case ConflictErrorErrorEnum.EmailAlreadyInUse:
            handleApiError('Create user api error', 'saveApiErrorMessageEmailInUse');
            break;
          case ConflictErrorErrorEnum.PhoneNumberAlreadyInUse:
            handleApiError('Create user api error', 'saveApiErrorMessagePhoneNumberInUse');
            break;
          default:
            handleApiError('Create user api error', 'saveUserError');
            break;
        }
        return;
      }

      updatedUser = savedUser as User;
    } else {
      // First move user to other group
      // ATM can't change other values like name or email or similar in this request
      const body = { user_ids: [user.id!] };

      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      const movedUsers = await moveUsersToUserGroup(body, user.user_group?.id!);
      if (!movedUsers) {
        handleApiError('Move user to group api error', 'saveUserError');
        return;
      }

      // Then after user group is changed then update invoicing account and employeeId for user in new user group
      const updatedUserAfterDetailsUpdate = await updateUserDetailsApi(user.invoicing_account?.id ?? null, employeeId ?? null, userGroupId!, user.id);
      if (updatedUserAfterDetailsUpdate === null || isAxiosError(updatedUserAfterDetailsUpdate)) {
        handleApiError('Update user details api error', 'saveUserError');
        return;
      }

      updatedUser = { ...updatedUserAfterDetailsUpdate, modified: false };
    }

    updatedUser.user_group = user.user_group; // ATM backend doesn't return user_group

    devLogger('Saved or moved user successfully', updatedUser);
    dispatch(updateUser(updatedUser, user.id));
  };

export const removeUser =
  (user: User): AppThunk<Promise<void>> =>
  async (dispatch: Dispatch) => {
    if (user.newItem) {
      dispatch(deleteUser(user.id!));
    } else {
      dispatch(usersRequestStart());

      const deleted = await deleteUserApi(user).catch(error => {
        if (isAxiosError(error) && error.response?.data?.status === 409) {
          switch (error.response.data.message) {
            case 'User has active assets':
              dispatch(showGrowlNotificationTranslated('apiErrors:user.deleteUserErrorActiveAssets', 'error'));
              break;
            case 'User is approver in approval chain':
              dispatch(showGrowlNotificationTranslated('apiErrors:user.deleteUserErrorApproverInApprovalChain', 'error'));
              break;
            case 'User has pending approval chain steps':
              dispatch(showGrowlNotificationTranslated('apiErrors:user.deleteUserErrorPendingApprovalChainSteps', 'error'));
              break;
            default:
              dispatch(showGrowlNotificationTranslated('apiErrors:user.deleteUserError', 'error'));
              break;
          }
        } else {
          dispatch(showGrowlNotificationTranslated('apiErrors:user.deleteUserError', 'error'));
        }
        dispatch(usersRequestError('Delete user api error'));
        return false;
      });

      if (!deleted) {
        return;
      }

      devLogger('User deleted', user);
      dispatch(deleteSavedUser(user.id!));
    }
  };
